/**
 * @voltxampere/web — Waveform cursor pair (Sprint 18 Paket C C1)
 *
 * Two draggable vertical cursors T1 / T2 over the oscilloscope
 * canvas. The store owns:
 *
 *   - per-cursor time values (`t1`, `t2`, seconds)
 *   - the `enabled` toggle (default ON; mockup shows them visible
 *     by default)
 *
 * Derived state (read-only):
 *
 *   - `cursorDeltaT`        — `|t2 − t1|`, seconds
 *   - `cursorInverseDeltaT` — `1 / Δt` Hz; null when Δt ≤ 0 so
 *                             consumers display a dash instead of
 *                             dividing by zero.
 *
 * Mutators clamp `t` to `Number.isFinite` (NaN / Infinity ignored
 * so a misbehaving pointer event cannot poison the store).
 *
 * The store stays UI-only — no engine touch — and `enabled` is
 * a clean toggle so disabling cursors freezes the readout but
 * preserves the last positions for the next enable.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, signal, type ReadonlySignal } from "@preact/signals-core";

export type CursorId = "t1" | "t2";

export interface WaveformCursorState {
  readonly t1: number;
  readonly t2: number;
  readonly enabled: boolean;
}

const INITIAL_T1 = 2.5e-3;
const INITIAL_T2 = 3.75e-3;

export const waveformCursors = signal<WaveformCursorState>({
  t1: INITIAL_T1,
  t2: INITIAL_T2,
  enabled: true,
});

export const cursorDeltaT: ReadonlySignal<number> = computed(() => {
  const c = waveformCursors.value;
  return Math.abs(c.t2 - c.t1);
});

export const cursorInverseDeltaT: ReadonlySignal<number | null> = computed(() => {
  const dt = cursorDeltaT.value;
  return dt > 0 ? 1 / dt : null;
});

export function setCursor(which: CursorId, t: number): void {
  if (!Number.isFinite(t)) return;
  const prev = waveformCursors.value;
  if (which === "t1") {
    if (prev.t1 === t) return;
    waveformCursors.value = { ...prev, t1: t };
  } else {
    if (prev.t2 === t) return;
    waveformCursors.value = { ...prev, t2: t };
  }
}

export function nudgeCursor(which: CursorId, deltaSeconds: number): void {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds === 0) return;
  const prev = waveformCursors.value;
  const next = (which === "t1" ? prev.t1 : prev.t2) + deltaSeconds;
  setCursor(which, next);
}

export function setCursorsEnabled(enabled: boolean): void {
  const prev = waveformCursors.value;
  if (prev.enabled === enabled) return;
  waveformCursors.value = { ...prev, enabled };
}

export function toggleCursors(): void {
  setCursorsEnabled(!waveformCursors.value.enabled);
}

/**
 * Snap a free-running time value to the nearest sample timestamp
 * in `times`. Returns the input unchanged if `times` is empty.
 *
 * `times` is assumed sorted ascending — callers pass the buffer
 * snapshot's `times` field directly. Binary search keeps the cost
 * at O(log n) for the typical 100k-sample transient sweep.
 */
export function snapTimeToSample(t: number, times: ArrayLike<number>): number {
  const n = times.length;
  if (n === 0) return t;
  const first = times[0];
  const last = times[n - 1];
  if (first === undefined || last === undefined) return t;
  if (t <= first) return first;
  if (t >= last) return last;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >>> 1;
    const candidate = times[mid] ?? t;
    if (candidate <= t) lo = mid;
    else hi = mid;
  }
  const left = times[lo] ?? t;
  const right = times[hi] ?? t;
  return Math.abs(t - left) <= Math.abs(t - right) ? left : right;
}
